const toNumber = (value) => {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/** "YYYY-MM-DD" → "DD.MM.YYYY" */
export const formatEntryDate = (value) => {
  if (!value) return "";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return String(value);
  return `${day}.${month}.${year}`;
};

/** project_dt → projectDt */
export const apiFieldToUi = (field) => {
  const match = String(field).match(/^(.*)_(dt|ct)$/);
  if (!match) return field;
  const camel = match[1].replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  return `${camel}${match[2] === "dt" ? "Dt" : "Kt"}`;
};

/** projectDt → project_dt, если колонки журнала ещё не пришли */
const uiFieldToAnalytic = (field) => {
  const match = String(field).match(/^(.*)(Dt|Kt)$/);
  if (!match) return null;
  const snake = match[1].replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);
  return `${snake}_${match[2] === "Dt" ? "dt" : "ct"}`;
};

let analyticKeyByUi = {};

/** Колонки GET /finance/journal/columns: UI-ключ → поле аналитики API */
export const setAnalyticKeyMap = (columns) => {
  const next = {};
  (columns?.analytics || []).forEach((column) => {
    (column.fields || []).forEach((field) => {
      next[apiFieldToUi(field)] = field;
    });
  });
  analyticKeyByUi = next;
};

const pushAnalytic = (list, label, value) => {
  if (value) list.push({ label, value });
};

const applyAnalytics = (target, analytics, titles) => {
  const analyticsDt = [];
  const analyticsKt = [];
  Object.entries(analytics || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    const uiKey = apiFieldToUi(key);
    target[uiKey] = value;
    const title = titles?.[key] || key.replace(/_(dt|ct)$/, "");
    if (key.endsWith("_dt")) analyticsDt.push({ label: title, value });
    else if (key.endsWith("_ct")) analyticsKt.push({ label: title, value });
  });
  target.analyticsDt = analyticsDt;
  target.analyticsKt = analyticsKt;
};

/**
 * Поля одного слоя (source / corr / mgmt) в ключах таблицы журнала.
 */
export const mapLayerFields = (layer, titles) => {
  const source = layer || {};
  const fields = {
    organization: source.organization || "",
    accountDt: source.account_dt || source.account_dt_code || null,
    accountKt: source.account_ct || source.account_ct_code || null,
    accountCodeDt: source.account_dt_code || null,
    accountCodeKt: source.account_ct_code || null,
    quantity: toNumber(source.quantity),
    amount: toNumber(source.amount),
    comment: source.comment || null,
    date: formatEntryDate(source.layer_date),
    updatedBy: source.updated_by || null,
    updatedAt: source.updated_at || null,
    subcontoDt: Array.isArray(source.subconto_dt) ? source.subconto_dt : [],
    subcontoKt: Array.isArray(source.subconto_ct) ? source.subconto_ct : [],
    mappingIssues: source.mapping_issues || {},
    appliedRules: source.applied_rules || {},
    excludedByRule: source.excluded_by_rule || null,
  };
  applyAnalytics(fields, source.analytics, titles);
  (fields.subcontoDt || []).forEach((value, index) => {
    if (value) pushAnalytic(fields.analyticsDt, `Субконто ${index + 1}`, value);
  });
  (fields.subcontoKt || []).forEach((value, index) => {
    if (value) pushAnalytic(fields.analyticsKt, `Субконто ${index + 1}`, value);
  });
  return fields;
};

const DIRECT_CORRECTION_FIELDS = {
  organization: "organization",
  accountDt: "account_dt",
  accountKt: "account_ct",
  amount: "amount",
  quantity: "quantity",
  correctionComment: "comment",
  comment: "comment",
  reason: "reason",
  layerDate: "layer_date",
};

/**
 * Поля формы корректировки → тело PATCH /finance/journal/{id}/corr.
 */
export const toCorrectionPayload = (fields, reason) => {
  const body = { analytics: {} };
  Object.entries(fields || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    const direct = DIRECT_CORRECTION_FIELDS[key];
    if (direct) {
      body[direct] = value;
      return;
    }
    const analytic = analyticKeyByUi[key] || uiFieldToAnalytic(key);
    if (analytic) body.analytics[analytic] = value;
  });
  if (!Object.keys(body.analytics).length) delete body.analytics;
  body.reason = reason || fields?.reason || null;
  return body;
};

/**
 * Проводка GET /finance/journal → строка журнала.
 * view: source | management | corrections — какой слой показать в таблице.
 */
export const mapJournalEntry = (item, view = "source") => {
  const sourceFields = mapLayerFields(item.source);
  const managementFields = mapLayerFields(item.mgmt);
  const correctionFields = mapLayerFields(item.corr);
  const primary = view === "source" || !item.mgmt ? sourceFields : managementFields;

  const issues = managementFields.mappingIssues || {};
  const hasIssues = Object.keys(issues).length > 0;

  return {
    ...primary,
    id: item.id,
    fromApi: true,
    date: primary.date || formatEntryDate(item.entry_date),
    document: item.document || item.document_purpose || "—",
    content: item.document_purpose || null,
    documentComment: item.document_comment || null,
    comment: primary.comment || item.document_comment || null,
    amount: primary.amount ?? toNumber(item.amount_kzt),
    quantity: primary.quantity ?? toNumber(item.quantity),
    source: item.source_system || "",
    dataType: item.data_type || "",
    budgetVersion: null,
    hasWarning: hasIssues || Boolean(managementFields.excludedByRule),
    warningTitle: managementFields.excludedByRule
      ? "Исключена правилом"
      : hasIssues
        ? "Есть нераспознанные значения"
        : undefined,
    corrAuthor: correctionFields.updatedBy,
    corrDate: formatEntryDate(correctionFields.updatedAt),
    layers: {
      source: {
        ...sourceFields,
        date: sourceFields.date || formatEntryDate(item.entry_date),
        amount: sourceFields.amount ?? toNumber(item.amount_kzt),
        quantity: sourceFields.quantity ?? toNumber(item.quantity),
        comment: sourceFields.comment || item.document_comment || null,
      },
      management: {
        ...managementFields,
        date: managementFields.date || formatEntryDate(item.entry_date),
        amount: managementFields.amount ?? toNumber(item.amount_kzt),
        quantity: managementFields.quantity ?? toNumber(item.quantity),
      },
      corrections: correctionFields,
    },
  };
};
